/**
 * Search (spec §14): instant local filter + 350 ms debounced server search merged with
 * `listed:false` rows, stale-response drop, saved/recent searches, bulk "act on all matches",
 * label filter, follow-ups pseudo-search and address suggestions.
 */
import { api, groups, isAborted } from '$lib/api/client';
import { db, rowKey } from '$lib/db';
import { FOLLOWUP_SCAN_ROWS, findFollowups, type Followup } from '$lib/mail/followups';
import { isActiveQuery, parseQuery, queryChips, type ParsedQuery } from '$lib/search/grammar';
import { filterLocal } from '$lib/search/local';
import { searchTerms as termsOf } from '$lib/search/highlight';
import { SUGGEST_LIMIT_COMPOSE, SUGGEST_SCAN_ROWS, buildInteractions, rankSuggestions } from '$lib/search/suggest';
import type { MessageRow } from '$lib/types';
import { debounce } from '$lib/util/debounce';
import type { MailStore } from './mail.svelte';
import { LS, lsDel, lsSet } from './state.svelte';

export const SEARCH_DEBOUNCE_MS = 350;
export const RECENT_MAX = 8;

export class SearchModule {
  private readonly debounced = debounce((q: ParsedQuery, key: string) => void this.runServer(q, key), SEARCH_DEBOUNCE_MS);

  constructor(private readonly s: MailStore) {}

  get parsed(): ParsedQuery {
    return parseQuery(this.s.searchQ);
  }

  /** Free-text words (≥ 2 chars) for highlighting — only while results are shown. */
  get terms(): string[] {
    return this.s.searchResults ? termsOf(this.s.searchQ) : [];
  }

  get searchLabel(): string | null {
    return this.parsed.label ?? null;
  }

  /** Called on every keystroke. */
  setSearch(q: string): void {
    this.s.searchQ = q;
    this.debounced.cancel();
    this.s.searchError = null;
    const parsed = parseQuery(q);
    this.s.searchChips = queryChips(parsed);
    if (!isActiveQuery(parsed)) {
      this.s.searchResults = null;
      this.s.searching = false;
      this.s.searchScanned = null;
      this.s.searchTruncated = false;
      return;
    }
    const local = filterLocal(this.s.msgs, parsed);
    this.s.searchResults = local;
    if (this.s.offline) return;
    this.s.searching = true;
    this.debounced(parsed, parsed.text.trim().toLowerCase());
  }

  private async runServer(parsed: ParsedQuery, key: string): Promise<void> {
    const folder = this.s.currentFolder;
    const stale = () => parseQuery(this.s.searchQ).text.trim().toLowerCase() !== key;
    try {
      const r = await groups.latest('search', (signal) => api.search(folder, parsed, { signal }));
      if (stale()) return;
      this.s.searchError = null;
      this.s.searchMatched = r.matched ?? 0;
      this.s.searchRanked = r.ranked ?? 'date';
      this.s.searchScanned = r.scanned ?? null;
      this.s.searchTruncated = !!r.truncated;
      const folders = [...new Set(r.messages.map((m) => m.folder || folder))];
      const cached = (await Promise.all(folders.map((f) => db.messages.where('folder').equals(f).toArray()))).flat();
      const rows: MessageRow[] = r.messages.map((m) => ({ ...this.s.list.mergeRow(this.s.list.toRow(m.folder || folder, m), cached), listed: false as const }));
      const local = this.s.searchResults ?? [];
      const have = new Set(local.map((m) => m.key));
      this.s.searchResults = [...local, ...rows.filter((m) => !have.has(m.key))].sort((a, b) => b.uid - a.uid);
      await db.messages.bulkPut(rows);
    } catch (err) {
      if (isAborted(err) || stale()) return;
      this.s.searchError = (err as { code?: string })?.code || 'search_failed';
    } finally {
      if (!stale()) this.s.searching = false;
    }
  }

  /** Toggle: same query → clear. */
  runSearch(q: string): void {
    if (this.s.searchQ.trim() === q) {
      this.clearSearch();
      return;
    }
    this.setSearch(q);
  }

  retrySearch(): void {
    const q = this.s.searchQ;
    this.s.searchError = null;
    if (isActiveQuery(parseQuery(q))) this.setSearch(q);
  }

  /** Reset state and drop search-only cached rows except the one being read. */
  clearSearch(): void {
    this.debounced.cancel();
    groups.cancel('search');
    this.s.followupOn = false;
    this.s.searchQ = '';
    this.s.searchResults = null;
    this.s.searching = false;
    this.s.searchMatched = 0;
    this.s.searchError = null;
    this.s.searchRanked = 'date';
    this.s.searchChips = [];
    this.s.searchScanned = null;
    this.s.searchTruncated = false;
    const folder = this.s.currentFolder;
    const keep = this.s.screen.name === 'read' ? rowKey(this.s.screen.folder, this.s.screen.uid) : null;
    void db.messages
      .where('folder')
      .equals(folder)
      .toArray()
      .then((rows) => {
        const gone = rows.filter((r) => r.listed === false && r.key !== keep).map((r) => r.key);
        return gone.length ? db.messages.bulkDelete(gone) : undefined;
      })
      .catch(() => undefined);
  }

  filterByLabel(label: string): void {
    const q = `label:${label}`;
    if (this.s.searchQ.trim() === q) this.clearSearch();
    else this.setSearch(q);
  }

  /** MRU of 8, min 2 chars. */
  rememberSearch(q: string): void {
    const v = q.trim();
    if (v.length < 2) return;
    const next = [v, ...this.s.recentSearches.filter((x) => x !== v)].slice(0, RECENT_MAX);
    this.s.recentSearches = next;
    lsSet(LS.recent, JSON.stringify(next));
  }

  forgetSearches(): void {
    this.s.recentSearches = [];
    lsDel(LS.recent);
  }

  async loadSearches(): Promise<void> {
    try {
      this.s.savedSearches = (await api.searches()).searches;
    } catch {
      /* offline */
    }
  }

  async saveSearch(name: string): Promise<void> {
    const q = this.s.searchQ.trim();
    if (!q || !name.trim()) return;
    try {
      this.s.savedSearches = (await api.addSearch(name.trim(), q)).searches;
      this.s.showToast('searchSaved');
    } catch (err) {
      this.s.showToast((err as { status?: number })?.status === 409 ? 'searchTooMany' : 'actionFailed');
    }
  }

  async removeSearch(id: string): Promise<void> {
    try {
      this.s.savedSearches = (await api.deleteSearch(id)).searches;
    } catch {
      this.s.showToast('actionFailed');
    }
  }

  /** POST /api/search/act with the parsed operators; `bulkTooMany` on `too_many`. */
  async actOnAllMatches(op: string, extra: Record<string, unknown> = {}): Promise<{ matched: number; affected: number } | null> {
    const q = this.parsed;
    if (!isActiveQuery(q)) return null;
    try {
      const r = await api.searchAct({
        folder: this.s.currentFolder,
        op,
        q: q.text,
        from: q.from,
        to: q.to,
        subject: q.subject,
        unseen: q.unseen,
        seen: q.seen,
        flagged: q.flagged,
        hasAttachment: q.hasAttachment,
        list: q.list,
        personal: q.personal,
        after: q.after,
        before: q.before,
        label: q.label,
        ...extra,
      });
      await this.s.syncFolder(this.s.currentFolder, { reconcile: true }).catch(() => undefined);
      this.s.clearSelection();
      this.s.showToast('bulkDone');
      this.s.lastBulk = r;
      return r;
    } catch (err) {
      const code = (err as { code?: string })?.code || '';
      this.s.showToast(code === 'too_many' ? 'bulkTooMany' : 'actionFailed');
      return null;
    }
  }

  // ---- follow-ups ---------------------------------------------------------------------------

  /** Wait for folders (≤ 15 s), then sync Sent and count follow-ups. */
  async bootFollowups(): Promise<void> {
    try {
      for (let i = 0; i < 30 && !this.s.folders.length; i++) await new Promise((r) => setTimeout(r, 500));
      const sent = this.s.sentPath;
      if (!sent) return;
      await new Promise((r) => setTimeout(r, 1500));
      await this.s.syncFolder(sent);
      await this.refreshFollowups();
    } catch {
      /* best effort */
    }
  }

  async refreshFollowups(): Promise<Followup[]> {
    const sent = this.s.sentPath;
    if (!sent) {
      this.s.followupCount = 0;
      return [];
    }
    const rows = await db.messages.orderBy('date').reverse().limit(FOLLOWUP_SCAN_ROWS).toArray();
    const list = findFollowups(rows.filter((r) => r.folder === sent), rows.filter((r) => r.folder !== sent), Date.now());
    this.s.followupCount = list.length;
    return list;
  }

  /** Toggle the follow-ups pseudo search (chip `followupChip`). */
  async showFollowups(): Promise<void> {
    if (this.s.followupOn) {
      this.s.followupOn = false;
      this.clearSearch();
      return;
    }
    const sent = this.s.sentPath;
    if (!sent) return;
    this.s.searching = true;
    try {
      await this.s.syncFolder(sent);
    } catch {
      /* show what is cached */
    }
    const list = await this.refreshFollowups();
    this.s.searchQ = '';
    this.s.searchChips = [{ key: 'followup', label: '' }];
    this.s.searchResults = list.map((f) => f.msg);
    this.s.searchMatched = list.length;
    this.s.searchError = null;
    this.s.searching = false;
    this.s.followupOn = true;
    this.s.navigate({ name: 'list', folder: this.s.currentFolder });
  }

  // ---- suggestions --------------------------------------------------------------------------

  /** Address suggestions for compose (6) or the search bar (3). */
  async suggest(needle: string, limit = SUGGEST_LIMIT_COMPOSE): Promise<{ name: string; email: string }[]> {
    const n = needle.trim().toLowerCase();
    if (!n) return [];
    const book = new Map<string, string>();
    for (const c of await db.contacts.toArray()) book.set(c.email.toLowerCase(), c.name);
    const rows = await db.messages.orderBy('date').reverse().limit(SUGGEST_SCAN_ROWS).toArray();
    return rankSuggestions({ needle: n, now: Date.now(), interactions: buildInteractions(rows, this.s.sentPath), book, me: this.s.user || '', limit }).map((x) => ({
      name: x.name,
      email: x.email,
    }));
  }
}
